import {
  Button,
  Checkbox,
  HStack,
  Input as ChakraInput,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Spacer,
  Tag,
  TagLabel,
  Text as ChakraText,
  VStack,
  Wrap,
  IconButton,
  Icon,
  Text,
} from '@chakra-ui/react'
import type { TFunction } from 'i18next'
import type { RefObject } from 'react'
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import ReactDOM from 'react-dom'
import { useTranslation } from 'react-i18next'
import type { UseComboboxReturnValue } from 'downshift'
import { IoIosArrowRoundBack } from 'react-icons/io'
import { colors } from '../../../../ui-kit'
import { FileType } from '../../../constants'
import useOutsideClickHandler from '../../../hooks/useOutsideClickHandler'
import type {
  CustomMetadataResponse,
  FormValidationObject,
  ListItemProps,
  RemoveFilterProps,
  SearchQueryJson,
} from '../../../interfaces'
import { MLContext } from '../../../providers'
import AdvancedSearch from '../AdvancedSearch'
import { MediaLibraryActions } from '../../../providers/MLProvider'
import SearchBarAutoCompleter from './SearchBarAutoCompleter'
import { SemanticImageSearch } from './SemanticImageSearch'

interface FileTypeObject {
  text: TFunction
  value: FileType
}

interface Props {
  searchInputRef: RefObject<any>
  searchInputActualRef: RefObject<any>
  isNameInputDisabled: boolean
  isFileTypeMenuDisabled: boolean
  nameValue: string
  isFolderRestrictedSearch: string
  fileType: FileType
  tempFileType: FileTypeObject
  versionIncluded: boolean
  setVersionIncluded(boolean): void
  fileTypeOptions: Array<{
    text: TFunction
    value: FileType
  }>
  searchQueryJson: SearchQueryJson
  customMetadataFields: CustomMetadataResponse[]
  customMetadataFieldsMapping: { [fieldName: string]: CustomMetadataResponse }
  validationObject: FormValidationObject
  inputBoxLeftElement: React.ReactElement
  inputBoxRightElement: React.ReactElement | null
  semanticSearch?: string
  setFileType(option: FileTypeObject): void
  setTempFileType(option: FileTypeObject): void
  setNameValue(string): void
  onSubmitSearch(): void
  setSearchFocused(boolean): void
  setSearchQueryJson(SearchQueryJson: SearchQueryJson): void
  removeFilter(removeFilterProps: RemoveFilterProps): void
  resetSearch(): void
  setSemanticSearch?(string): void
  setIsFolderRestrictedSearch(string): void
}

const SearchMenu = ({
  setSearchFocused,
  searchInputRef,
  searchInputActualRef,
  nameValue,
  fileType,
  tempFileType,
  setTempFileType,
  versionIncluded,
  setVersionIncluded,
  isNameInputDisabled,
  isFileTypeMenuDisabled,
  fileTypeOptions,
  inputBoxLeftElement,
  inputBoxRightElement,
  setFileType,
  setNameValue,
  onSubmitSearch,
  searchQueryJson,
  setSearchQueryJson,
  customMetadataFields,
  customMetadataFieldsMapping,
  validationObject,
  removeFilter,
  resetSearch,
  semanticSearch,
  setSemanticSearch,
  setIsFolderRestrictedSearch,
  isFolderRestrictedSearch,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const [
    {
      isVersionIncludedInSearch,
      openAdvancedSearch,
      openSemanticImageSearch,
      folderPath,
    },
    dispatch,
  ] = useContext(MLContext)
  const outsideClickRef = useRef(null)
  const maxWidthRef = useRef(null)
  const localInputRef = useRef<HTMLInputElement>(null)
  const [submitFlag, setSubmitFlag] = useState<boolean>(false)

  const shouldResetOpenAdvancedSearch = useCallback(
    () =>
      tempFileType.value === FileType.all &&
      fileType === FileType.all &&
      !searchQueryJson.filterGroups.length &&
      !isVersionIncludedInSearch &&
      isFolderRestrictedSearch === 'false',
    [
      tempFileType,
      fileType,
      searchQueryJson,
      isVersionIncludedInSearch,
      isFolderRestrictedSearch,
    ],
  )

  useOutsideClickHandler({
    ref: outsideClickRef,
    handler: () => {
      void setSearchFocused(false)
      if (shouldResetOpenAdvancedSearch()) {
        dispatch([
          {
            type: MediaLibraryActions.SET_OPEN_ADVANCED_SEARCH,
            payload: false,
          },
        ])
      }

      dispatch([
        {
          type: MediaLibraryActions.SET_OPEN_SEMANTIC_IMAGE_SEARCH,
          payload: false,
        },
      ])
    },
    dependencies: [shouldResetOpenAdvancedSearch],
  })

  const inputRect = useMemo<DOMRect | undefined>(
    () => searchInputRef.current?.getBoundingClientRect(),
    [searchInputRef],
  )

  const inputActualRect = useMemo<DOMRect | undefined>(
    () => searchInputActualRef.current?.getBoundingClientRect(),
    [searchInputActualRef],
  )
  const focusInput = useCallback(() => {
    if (localInputRef.current) {
      const isDisabled = localInputRef.current.disabled
      localInputRef.current.disabled = false
      localInputRef.current.focus()
      localInputRef.current.disabled = isDisabled
    }
  }, [localInputRef])

  useEffect(() => {
    setTimeout(() => void focusInput())
  }, [])

  useEffect(() => {
    if (submitFlag) {
      setSubmitFlag(false)
      onSubmitSearch()
      void setSearchFocused(false)
    }
  }, [submitFlag])

  if (openAdvancedSearch) {
    return ReactDOM.createPortal(
      <VStack
        ref={outsideClickRef}
        spacing="0"
        zIndex={1401}
        position="fixed"
        width="60vw"
        height="70vh"
        bgColor={colors.appWhite}
        top={inputRect?.top ? `${inputRect.top}px` : 'initial'}
        left={inputRect?.left ? `${inputRect.left}px` : 'initial'}
        borderRadius="0.25rem"
        borderWidth="1px"
        borderColor="#e6e7ea"
        overflow="auto"
      >
        <InputGroup>
          <InputLeftElement
            children={inputBoxLeftElement}
            paddingLeft="0.5em"
            position="static"
            width="unset"
            borderBottomWidth="1px"
            borderBottomColor={
              isNameInputDisabled ? 'rgba(230, 231, 234, 0.4)' : undefined
            }
            bgColor={
              isNameInputDisabled ? 'rgba(242, 242, 245, 0.4)' : undefined
            }
            cursor="default"
          />
          <ChakraInput
            placeholder={t('file_or_folder_name', 'File or folder name')}
            ref={localInputRef}
            value={isNameInputDisabled ? '' : nameValue}
            onChange={e => void setNameValue(e.target.value)}
            onKeyPress={e => {
              if (e.key === 'Enter') {
                dispatch([
                  {
                    type: MediaLibraryActions.SET_VERSION_INCLUDED_IN_SEARCH,
                    payload: versionIncluded,
                  },
                ])
                setFileType(tempFileType)
                setSubmitFlag(true)
              }
            }}
            isDisabled={isNameInputDisabled}
            borderWidth="0"
            borderBottomWidth="1px"
            borderBottomColor="#e6e7ea"
            borderBottomRadius="none"
            focusBorderColor="none"
            _hover={{ borderColor: '#e6e7ea' }}
            position="static"
            paddingLeft={
              tempFileType.value !== FileType.all ? '0.5em' : 'unset'
            }
          />
          {inputBoxRightElement ? (
            <InputRightElement width="unset" children={inputBoxRightElement} />
          ) : null}
        </InputGroup>
        <>
          <VStack
            width="full"
            alignItems="start"
            spacing="1.5em"
            paddingX={10}
            paddingY="1.5em"
          >
            {folderPath && folderPath !== '/' && (
              <VStack
                width="full"
                alignItems="start"
                spacing="0.5em"
                paddingLeft="0.5em"
              >
                <ChakraText color={colors.appSlate} fontWeight="semibold">
                  {t('location', 'Location')}
                </ChakraText>
                <Checkbox
                  isChecked={isFolderRestrictedSearch === 'true'}
                  onChange={e => {
                    setIsFolderRestrictedSearch(
                      e.target.checked ? 'true' : 'false',
                    )
                  }}
                >
                  {t('limit_search_to_folder', 'Limit search to this folder')}
                </Checkbox>
              </VStack>
            )}
            <VStack
              width="full"
              alignItems="start"
              spacing="0.5em"
              paddingLeft="0.5em"
            >
              <ChakraText color={colors.appSlate} fontWeight="semibold">
                {t('search_within', 'Search within')}
              </ChakraText>

              <Wrap shouldWrapChildren spacing="0.5em">
                {fileTypeOptions.map(fileTypeLocal => (
                  <Tag
                    padding="0.5em"
                    cursor={isFileTypeMenuDisabled ? 'not-allowed' : 'pointer'}
                    _hover={
                      isFileTypeMenuDisabled
                        ? {}
                        : { bgColor: colors.appBlueyGrey }
                    }
                    opacity={isFileTypeMenuDisabled ? '0.6' : '1'}
                    onClick={() => {
                      if (!isFileTypeMenuDisabled) {
                        void setTempFileType(fileTypeLocal)
                        void focusInput()
                      }
                    }}
                  >
                    <TagLabel color={colors.appSlate}>
                      {fileTypeLocal.text}
                    </TagLabel>
                  </Tag>
                ))}
              </Wrap>
            </VStack>

            <VStack align="start" paddingLeft="0.5em">
              <ChakraText color={colors.appSlate} fontWeight="semibold">
                {t('file_versions_', 'File versions')}
              </ChakraText>
              <Checkbox
                isChecked={versionIncluded}
                onChange={e => void setVersionIncluded(e.target.checked)}
                onKeyPress={e => {
                  if (e.key === 'Enter') {
                    dispatch([
                      {
                        type: MediaLibraryActions.SET_VERSION_INCLUDED_IN_SEARCH,
                        payload: versionIncluded,
                      },
                    ])
                    setFileType(tempFileType)
                    setSubmitFlag(true)
                  }
                }}
              >
                {t(
                  'include_versions_in_results',
                  'Include file versions in search results',
                )}
              </Checkbox>
            </VStack>

            <VStack
              ref={maxWidthRef}
              width="full"
              alignItems="start"
              spacing="0.5em"
              paddingLeft="0.5em"
            >
              <ChakraText
                color={colors.appSlate}
                fontWeight="semibold"
                marginBottom="-0.5em"
              >
                {t('advanced_search', 'Advanced search')}
              </ChakraText>

              <AdvancedSearch
                {...{
                  searchQueryJson,
                  setSearchQueryJson,
                  customMetadataFields,
                  customMetadataFieldsMapping,
                  validationObject,
                  maxWidthRef,
                  onSubmitSearch,
                  removeFilter,
                }}
              />
            </VStack>
          </VStack>

          <Spacer />

          <HStack
            flexDirection="row-reverse"
            spacing="1em"
            paddingX={10}
            paddingY="1.5em"
            alignItems="start"
            width="full"
            borderTopWidth="1px"
            borderTopColor="#e6e7ea"
          >
            <Button
              onClick={() => {
                dispatch([
                  {
                    type: MediaLibraryActions.SET_VERSION_INCLUDED_IN_SEARCH,
                    payload: versionIncluded,
                  },
                ])
                setFileType(tempFileType)
                setSubmitFlag(true)
                if (shouldResetOpenAdvancedSearch()) {
                  dispatch([
                    {
                      type: MediaLibraryActions.SET_OPEN_ADVANCED_SEARCH,
                      payload: false,
                    },
                  ])
                }
              }}
              isDisabled={!validationObject.isValid}
              marginLeft="1em"
            >
              {t('search', 'Search')}
            </Button>

            <Button
              variant="outline"
              onClick={() => {
                void setSearchFocused(false)
                if (shouldResetOpenAdvancedSearch()) {
                  dispatch([
                    {
                      type: MediaLibraryActions.SET_OPEN_ADVANCED_SEARCH,
                      payload: false,
                    },
                  ])
                }
              }}
            >
              {t('close', 'Close')}
            </Button>

            <Button
              variant="outline"
              color={colors.appBrick}
              onClick={() => {
                void resetSearch()
                dispatch([
                  {
                    type: MediaLibraryActions.SET_OPEN_ADVANCED_SEARCH,
                    payload: false,
                  },
                ])
                void setSearchFocused(false)
              }}
            >
              {t('reset', 'Reset')}
            </Button>
          </HStack>
        </>
      </VStack>,
      document.body,
    )
  }

  if (openSemanticImageSearch) {
    return ReactDOM.createPortal(
      <VStack
        ref={outsideClickRef}
        spacing="0"
        zIndex={1401}
        position="fixed"
        width="58vw"
        maxHeight="70vh"
        px="4"
        paddingTop="6px"
        gap="12px"
        paddingBottom="24px"
        bgColor={colors.appWhite}
        top={inputRect?.top ? `${inputRect.top}px` : 'initial'}
        left={inputRect?.left ? `${inputRect.left}px` : 'initial'}
        borderRadius="0.25rem"
        borderWidth="1px"
        borderColor="#e6e7ea"
        overflow="auto"
      >
        <SemanticImageSearch setSearchFocused={setSearchFocused} />
      </VStack>,
      document.body,
    )
  }

  return (
    <SearchBarAutoCompleter
      page=""
      outsideClickRef={outsideClickRef}
      nameValue={nameValue}
      inputRect={inputRect}
      inputActualRect={inputActualRect}
      inputBoxRightElement={inputBoxRightElement}
      inputBoxLeftElement={inputBoxLeftElement}
      setNameValue={setNameValue}
      setSubmitFlag={setSubmitFlag}
      setSearchQueryJson={setSearchQueryJson}
      semanticSearch={semanticSearch}
      setSemanticSearch={setSemanticSearch}
      setSearchFocused={setSearchFocused}
    />
  )
}

export default SearchMenu
